import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";

/**
 * A single control/implementation row. `cells` aligns positionally with the
 * `columns` header array; the first cell is treated as the row's emphasized term
 * (rendered semibold + as the card title on mobile).
 */
export interface ControlRow {
	/** Stable key for React. */
	id: string;
	/** Cell values, positionally aligned with `columns`. */
	cells: ReadonlyArray<ReactNode>;
}

interface ControlTableProps {
	/** Column headers (e.g. ["Control", "Implementation"]). */
	columns: ReadonlyArray<string>;
	rows: ReadonlyArray<ControlRow>;
	/** Optional caption for screen readers / context. */
	caption?: string;
	className?: string;
}

/**
 * A premium control/implementation table — the bespoke replacement for the source
 * docs' multi-column control tables. Above `md` it renders a semantic table styled
 * like SubprocessorTable (uppercase tracked headers, hover rows, glass surface);
 * below `md` it collapses each row to a stacked card (term as the title, remaining
 * columns as labeled key/value pairs) so nothing overflows on mobile.
 */
export function ControlTable({ columns, rows, caption, className }: ControlTableProps) {
	return (
		// Warm hairline frame at the brand 20px radius. The header wears the brand's
		// lavender band (the `--accent` tint) with deep-purple labels, and body rows
		// carry a faint lavender zebra — the resources-hub table treatment.
		<div className={cn("overflow-hidden rounded-2xl border border-border", className)}>
			{/* Desktop / tablet: semantic table. */}
			<div className="hidden md:block">
				<Table>
					{caption && <caption className="sr-only">{caption}</caption>}
					<TableHeader>
						<TableRow className="border-border bg-accent hover:bg-accent">
							{columns.map((column, index) => (
								<TableHead
									key={column}
									className={cn(
										"h-10 px-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-foreground",
										index === 0 && "w-[28%]",
									)}
								>
									{column}
								</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map((row) => (
							<TableRow
								key={row.id}
								className="border-border/60 transition-colors even:bg-accent/40 hover:bg-accent/70"
							>
								{row.cells.map((cell, index) => (
									<TableCell
										key={columns[index] ?? index}
										className={cn(
											"px-5 py-3.5 align-top",
											index === 0 ? "font-medium text-foreground" : "text-muted-foreground",
										)}
									>
										{cell}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			{/* Mobile: stacked card list — first cell is the title, rest are key/value. */}
			<ul data-prose-skip className="divide-y divide-border md:hidden">
				{rows.map((row) => (
					<li key={row.id} className="p-5">
						<div className="font-medium text-foreground">{row.cells[0]}</div>
						<dl className="mt-3 space-y-2.5">
							{row.cells.slice(1).map((cell, index) => (
								<div key={columns[index + 1] ?? index}>
									<dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
										{columns[index + 1]}
									</dt>
									<dd className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{cell}</dd>
								</div>
							))}
						</dl>
					</li>
				))}
			</ul>
		</div>
	);
}
